import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Copy, Download, FileCode, Terminal, Info, ChevronDown, Check } from 'lucide-react';
import { Model } from '../../../types/chat';
import {
  buildIdeTools,
  buildSetupScript,
  scriptFileName,
  CONFIG_BASE_URL,
  CONFIG_MODEL_PLACEHOLDER,
  type ScriptPlatform,
  type IdeEnvVar,
} from '../../../constants/ideConfigs';

interface ConfigExportModalProps {
  open: boolean;
  onClose: () => void;
  apiKey: string;
  isRealKey: boolean;
  initialMode?: 'files' | 'script';
  models: Model[];
  copyToClipboard: (text: string) => void;
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

type SetupOs = 'windows' | 'macos' | 'linux';

function detectDefaultOs(): SetupOs {
  if (typeof navigator === 'undefined') return 'windows';
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mac')) return 'macos';
  if (ua.includes('win')) return 'windows';
  return 'linux';
}

function EnvVarSetupGuide({
  envVars,
  copyToClipboard,
}: {
  envVars: IdeEnvVar[];
  copyToClipboard: (text: string) => void;
}) {
  const { t } = useTranslation(['settings', 'common']);
  const [os, setOs] = useState<SetupOs>(detectDefaultOs);

  const osTabs: { id: SetupOs; label: string }[] = [
    { id: 'windows', label: 'Windows' },
    { id: 'macos', label: 'macOS' },
    { id: 'linux', label: 'Linux' },
  ];

  const renderCommand = (command: string) => (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2">
      <code className="text-[13px] font-mono text-zinc-300 truncate">{command}</code>
      <button
        type="button"
        onClick={() => copyToClipboard(command)}
        className="p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-md transition-colors flex-shrink-0"
        title={t('common:copy')}
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  return (
    <div className="mt-3 rounded-xl border border-border bg-muted/40 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">
          {t('api_keys.config_export.env_setup_title')}
        </span>
      </div>

      <div className="inline-flex items-center bg-muted p-1 rounded-xl border border-border/50">
        {osTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setOs(tab.id)}
            className={`py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
              os === tab.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {envVars.map((env) => (
        <div key={env.name} className="space-y-2">
          {os === 'windows' && (
            <>
              <p className="text-[13px] text-muted-foreground">{t('api_keys.config_export.env_win_intro')}</p>
              {renderCommand(`setx ${env.name} "${env.value}"`)}
              <p className="text-[12px] text-muted-foreground">{t('api_keys.config_export.env_win_note')}</p>
            </>
          )}
          {os === 'macos' && (
            <>
              <p className="text-[13px] text-muted-foreground">{t('api_keys.config_export.env_mac_intro')}</p>
              {renderCommand(`echo 'export ${env.name}="${env.value}"' >> ~/.zshrc && source ~/.zshrc`)}
              <p className="text-[12px] text-muted-foreground">{t('api_keys.config_export.env_mac_gui_note')}</p>
              {renderCommand(`launchctl setenv ${env.name} "${env.value}"`)}
            </>
          )}
          {os === 'linux' && (
            <>
              <p className="text-[13px] text-muted-foreground">{t('api_keys.config_export.env_linux_intro')}</p>
              {renderCommand(`echo 'export ${env.name}="${env.value}"' >> ~/.bashrc && source ~/.bashrc`)}
              <p className="text-[12px] text-muted-foreground">{t('api_keys.config_export.env_linux_note')}</p>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function ConfigFileLocations({ path }: { path: string }) {
  const { t } = useTranslation(['settings']);
  if (!path.startsWith('~/')) return null;
  const rel = path.slice(2);
  const user = t('api_keys.config_export.username_placeholder');
  const rows: { label: string; value: string }[] = [
    { label: 'Windows', value: `C:\\Users\\${user}\\${rel.replace(/\//g, '\\')}` },
    { label: 'macOS', value: `/Users/${user}/${rel}` },
    { label: 'Linux', value: `/home/${user}/${rel}` },
  ];
  return (
    <div className="mt-2 rounded-xl border border-border bg-muted/40 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">
          {t('api_keys.config_export.config_file_location_title')}
        </span>
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        {rows.map((row, index) => (
          <div
            key={row.label}
            className={`flex items-center gap-3 px-3 py-2 ${index > 0 ? 'border-t border-border' : ''}`}
          >
            <span className="text-[12px] font-medium text-muted-foreground w-16 flex-shrink-0">
              {row.label}
            </span>
            <code className="text-[13px] font-mono text-foreground truncate">{row.value}</code>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ConfigExportModal({
  open,
  onClose,
  apiKey,
  isRealKey,
  initialMode,
  models,
  copyToClipboard,
}: ConfigExportModalProps) {
  const { t } = useTranslation(['settings', 'common']);
  const [activeToolId, setActiveToolId] = useState('codex');
  const [mode, setMode] = useState<'files' | 'script'>('files');
  const [platform, setPlatform] = useState<ScriptPlatform>('bash');
  const [selectedModel, setSelectedModel] = useState('');
  const [modelMenuOpen, setModelMenuOpen] = useState(false);

  const modelOptions = useMemo(() => {
    const ids = models.map((m) => m.id).filter(Boolean);
    return ids.length ? ids : [CONFIG_MODEL_PLACEHOLDER];
  }, [models]);

  useEffect(() => {
    if (open) {
      setSelectedModel((prev) => (prev && modelOptions.includes(prev) ? prev : modelOptions[0]));
    }
  }, [open, modelOptions]);

  useEffect(() => {
    if (open) {
      setMode(initialMode ?? 'files');
      setActiveToolId('codex');
    }
  }, [open, initialMode]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const tools = useMemo(
    () => buildIdeTools({ apiKey, modelId: selectedModel || modelOptions[0] }),
    [apiKey, selectedModel, modelOptions],
  );

  const tool = tools.find((item) => item.id === activeToolId) ?? tools[0];
  const effectiveMode = tool.supportsScript ? mode : 'files';
  const script = tool.supportsScript ? buildSetupScript(tool, platform) : '';

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto overscroll-contain"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      tabIndex={-1}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <FileCode className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground tracking-[-0.04em]">
                  {t('api_keys.config_export.title')}
                </h2>
                <p className="text-[13px] text-muted-foreground mt-0.5">
                  {t('api_keys.config_export.subtitle')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              title={t('common:close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto overscroll-contain space-y-5">
          {/* Key hint */}
          <div
            className={`flex items-start gap-2.5 rounded-xl border p-3 ${
              isRealKey
                ? 'bg-emerald-500/10 border-emerald-500/20'
                : 'bg-amber-500/10 border-amber-500/20'
            }`}
          >
            <Info
              className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                isRealKey ? 'text-emerald-500' : 'text-amber-500'
              }`}
            />
            <p
              className={`text-[13px] ${
                isRealKey
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-amber-600 dark:text-amber-400'
              }`}
            >
              {isRealKey
                ? t('api_keys.config_export.realkey_hint')
                : t('api_keys.config_export.placeholder_hint', { placeholder: apiKey })}
            </p>
          </div>

          {/* Base URL + model selector */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
                {t('api_keys.base_url_label')}
              </label>
              <div className="bg-muted border border-border rounded-lg px-3 py-2.5">
                <code className="text-[13px] font-mono text-foreground">{`${CONFIG_BASE_URL}/v1`}</code>
              </div>
            </div>
            <div>
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
                {t('api_keys.config_export.model_label')}
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setModelMenuOpen((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 bg-muted border border-border rounded-lg px-3 py-2.5 text-left hover:bg-muted/70 transition-colors"
                >
                  <code className="text-[13px] font-mono text-foreground truncate">
                    {selectedModel || modelOptions[0]}
                  </code>
                  <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </button>
                {modelMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setModelMenuOpen(false)} />
                    <div className="absolute left-0 right-0 mt-1.5 z-20 max-h-56 overflow-y-auto rounded-lg border border-border bg-card shadow-lg py-1">
                      {modelOptions.map((id) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => {
                            setSelectedModel(id);
                            setModelMenuOpen(false);
                          }}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[13px] font-mono text-foreground hover:bg-muted transition-colors"
                        >
                          <span className="truncate">{id}</span>
                          {(selectedModel || modelOptions[0]) === id && (
                            <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Tool tabs */}
          <div>
            <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
              {t('api_keys.config_export.tool_label')}
            </label>
            <div className="flex flex-wrap gap-2">
              {tools.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveToolId(item.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[13px] font-medium transition-all ${
                    activeToolId === item.id
                      ? 'bg-primary/10 border-primary/30 text-foreground'
                      : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <img
                    src={item.logo}
                    alt=""
                    className="h-4 w-4 object-contain"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {/* Endpoint + mode toggle */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-[12px] text-muted-foreground">
              {t('api_keys.config_export.endpoint_label')}:{' '}
              <code className="font-mono text-foreground">{tool.endpoint}</code>
            </div>
            {tool.supportsScript && (
              <div className="inline-flex items-center bg-muted p-1 rounded-xl border border-border/50">
                <button
                  type="button"
                  onClick={() => setMode('files')}
                  className={`flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
                    effectiveMode === 'files'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FileCode className="h-3.5 w-3.5" />
                  {t('api_keys.config_export.mode_files')}
                </button>
                <button
                  type="button"
                  onClick={() => setMode('script')}
                  className={`flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
                    effectiveMode === 'script'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Terminal className="h-3.5 w-3.5" />
                  {t('api_keys.config_export.mode_script')}
                </button>
              </div>
            )}
          </div>

          {/* UI-only tools (table) */}
          {tool.files.length === 0 && tool.table.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-start gap-2.5 rounded-xl border border-border bg-muted/40 p-3">
                <Info className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-[13px] text-muted-foreground">
                  {t('api_keys.config_export.table_only_note', { name: tool.name })}
                </p>
              </div>
              <div className="rounded-xl border border-border overflow-hidden">
                {tool.table.map((row, index) => (
                  <div
                    key={row.label}
                    className={`flex items-center justify-between gap-3 px-4 py-2.5 ${
                      index > 0 ? 'border-t border-border' : ''
                    }`}
                  >
                    <span className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">
                      {row.label}
                    </span>
                    <div className="flex items-center gap-2 min-w-0">
                      <code className="text-[13px] font-mono text-foreground truncate">{row.value}</code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(row.value)}
                        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors flex-shrink-0"
                        title={t('common:copy')}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Files mode */}
          {effectiveMode === 'files' && tool.files.length > 0 && (
            <div className="space-y-4">
              {tool.files.map((file) => (
                <div key={file.path} className="space-y-2">
                  <div className="rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-900 border-b border-zinc-800">
                      <code className="text-[13px] font-mono text-zinc-300">{file.path}</code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(file.content)}
                        className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
                        title={t('common:copy')}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <pre className="text-zinc-300 p-4 text-[13px] overflow-x-auto font-mono leading-relaxed">
                      {file.content}
                    </pre>
                  </div>
                  <ConfigFileLocations path={file.path} />
                </div>
              ))}

              {tool.envVars.length > 0 && (
                <div>
                  <div className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                    {t('api_keys.config_export.env_var_label')}
                  </div>
                  <div className="rounded-xl border border-border overflow-hidden">
                    {tool.envVars.map((env, index) => (
                      <div
                        key={env.name}
                        className={`flex items-center justify-between gap-3 px-4 py-2.5 ${
                          index > 0 ? 'border-t border-border' : ''
                        }`}
                      >
                        <code className="text-[13px] font-mono text-foreground truncate">
                          {env.name}={env.value}
                        </code>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(`${env.name}=${env.value}`)}
                          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors flex-shrink-0"
                          title={t('common:copy')}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <EnvVarSetupGuide envVars={tool.envVars} copyToClipboard={copyToClipboard} />
                </div>
              )}
            </div>
          )}

          {/* Script mode */}
          {effectiveMode === 'script' && tool.supportsScript && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="inline-flex items-center bg-muted p-1 rounded-xl border border-border/50">
                  <button
                    type="button"
                    onClick={() => setPlatform('bash')}
                    className={`py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
                      platform === 'bash'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('api_keys.config_export.platform_bash')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPlatform('powershell')}
                    className={`py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
                      platform === 'powershell'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('api_keys.config_export.platform_powershell')}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => copyToClipboard(script)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-muted-foreground hover:text-foreground rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {t('common:copy')}
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadText(scriptFileName(tool, platform), script)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {t('api_keys.config_export.download')}
                  </button>
                </div>
              </div>

              <div className="flex items-start gap-2.5 rounded-xl border border-border bg-muted/40 p-3">
                <Info className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-[13px] text-muted-foreground">
                  {t('api_keys.config_export.overwrite_note')}
                </p>
              </div>

              <div className="rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950">
                <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-900 border-b border-zinc-800">
                  <code className="text-[13px] font-mono text-zinc-400">
                    {scriptFileName(tool, platform)}
                  </code>
                </div>
                <pre className="text-zinc-300 p-4 text-[13px] overflow-x-auto font-mono leading-relaxed">
                  {script}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
